import React, { useState } from "react";
import { toast } from "react-toastify";
import Pagination from "../../../../atoms/Table/Pagination";
import CancelSessionModal from "../../components/CancelSessionModal/CancelSessionModal";
import DeleteDraftModal from "../../components/DeleteDraftModal/DeleteDraftModal";
import SessionDrawer from "../../components/SessionDrawer/SessionDrawer";
import WebinarFilters from "../../components/WebinarFilters/WebinarFilters";
import WebinarTable from "../../components/WebinarTable/WebinarTable";
import { useChiefCoachAccess } from "../../hooks/useChiefCoachAccess";
import { useSessionDrawer } from "../../hooks/useSessionDrawer";
import { useSessionMutations } from "../../hooks/useSessionMutations";
import { useSessionPrimaryAction } from "../../hooks/useSessionPrimaryAction";
import { useWebinarData } from "../../hooks/useWebinarData";
import {
  useWebinarFilters,
} from "../../hooks/useWebinarFilters";
import { WebinarSession } from "../../types/models";
import "./WebinarListPage.scss";

const WebinarListPage: React.FC = () => {
  const { isChiefCoach, ready: roleReady } = useChiefCoachAccess();
  const canManage = isChiefCoach;

  const {
    status,
    setStatus,
    q,
    setQ,
    debouncedQ,
    page,
    setPage,
    limit,
    setLimit,
  } = useWebinarFilters();

  const {
    sessions,
    topics,
    counts,
    total,
    loading,
    refreshing,
    error,
    refresh,
  } = useWebinarData({ status, q: debouncedQ, page, limit });

  const { drawer, openCreate, openEdit, openDuplicate, closeDrawer } =
    useSessionDrawer();

  const [cancelTarget, setCancelTarget] = useState<WebinarSession | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<WebinarSession | null>(
    null
  );

  const {
    submitting,
    cancelSession,
    deleteDraft,
    reinstateSession,
    copyJoinLink,
    shareSession,
  } = useSessionMutations({ onSuccess: refresh });

  const handlePrimary = useSessionPrimaryAction({
    openEdit,
    copyJoinLink,
    reinstateSession,
    canManage,
  });

  const guardManage = (action: () => void, label: string) => {
    if (!canManage) {
      toast.info(`Only a chief coach can ${label}`);
      return;
    }
    action();
  };

  const confirmCancel = async ({
    reason,
    note,
  }: {
    reason: string;
    note: string;
  }) => {
    if (!cancelTarget || !canManage) return;
    const ok = await cancelSession(cancelTarget, { reason, note });
    if (ok) setCancelTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !canManage) return;
    const ok = await deleteDraft(deleteTarget);
    if (ok) setDeleteTarget(null);
  };

  const handleStatusChange = (next: typeof status) => {
    // Always hit GET /posts — including re-clicking the active filter.
    if (next === status && page === 1 && !q.trim()) {
      refresh();
      return;
    }
    setStatus(next);
  };

  const listLoading = loading || !roleReady;
  const isCatalogEmpty = status === "all" && !debouncedQ.trim() && total === 0;

  return (
    <div className="wf-list-page">
      <header className="wf-list-page__header">
        <div>
          <h1>Live Sessions</h1>
          <p>Plan, publish and run GoodFlip live sessions for members.</p>
        </div>
        {canManage ? (
          <button
            type="button"
            className="wf-btn dark"
            onClick={() => guardManage(openCreate, "create sessions")}
          >
            New session
          </button>
        ) : null}
      </header>

      <WebinarFilters
        status={status}
        q={q}
        counts={counts}
        onStatusChange={handleStatusChange}
        onQueryChange={setQ}
      />

      {listLoading ? (
        <div className="wf-state">Loading sessions…</div>
      ) : error ? (
        <div className="wf-state wf-state--error">
          <p>{error}</p>
          <button type="button" className="wf-btn ghost" onClick={refresh}>
            Retry
          </button>
        </div>
      ) : (
        <div
          className={`wf-list-page__results${
            refreshing ? " is-refreshing" : ""
          }`}
          aria-busy={refreshing}
        >
          {refreshing ? (
            <div className="wf-list-page__refresh" role="status">
              <span className="wf-list-page__spinner" aria-hidden />
              <span className="wf-sr-only">Updating sessions…</span>
            </div>
          ) : null}
          <WebinarTable
            sessions={sessions}
            topics={topics}
            isCatalogEmpty={isCatalogEmpty}
            canManage={canManage}
            showShareColumn={status !== "cancelled" && status !== "ended"}
            onPrimary={handlePrimary}
            onEdit={(s) => guardManage(() => openEdit(s), "edit sessions")}
            onDuplicate={(s) =>
              guardManage(() => openDuplicate(s), "duplicate sessions")
            }
            onCancel={(s) =>
              guardManage(() => setCancelTarget(s), "cancel sessions")
            }
            onDeleteDraft={(s) =>
              guardManage(() => setDeleteTarget(s), "delete drafts")
            }
            onShare={shareSession}
            onCreate={
              canManage
                ? () => guardManage(openCreate, "create sessions")
                : undefined
            }
          />
          {total > 10 ? (
            <div className="wf-list-page__pagination">
              <Pagination
                totalRecords={total}
                pageSize={limit}
                currentPage={page}
                dataLength={sessions.length}
                onPageChange={setPage}
                onPageSizeChange={setLimit}
              />
            </div>
          ) : null}
        </div>
      )}

      <CancelSessionModal
        isOpen={!!cancelTarget}
        sessionTitle={cancelTarget?.title}
        submitting={submitting}
        onClose={() => setCancelTarget(null)}
        onConfirm={confirmCancel}
      />
      <DeleteDraftModal
        isOpen={!!deleteTarget}
        sessionTitle={deleteTarget?.title}
        submitting={submitting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      {drawer && canManage ? (
        <SessionDrawer
          open
          mode={drawer.mode}
          session={drawer.session}
          existingSessions={sessions}
          onClose={closeDrawer}
          onSaved={refresh}
        />
      ) : null}
    </div>
  );
};

export default WebinarListPage;
